import { AlertDangerIcon, Button, css, Feedback, Inline, styled } from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { ValueBox, ValueInput } from '@candu-ai/craft/ui';
import { PlaceholderParagraph } from 'commons/containers/MediaLibrary';
import DashedOutline from 'craft/components/DashedOutline';
import { IFrame as IFrameIcon } from 'craft/icons';

const Container = styled(DashedOutline)`
  min-width: 120px;
  min-height: 120px;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
`;

const InlineMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: ${theme.spaces.xxs};
  `}
`;

interface EmptyStateProps {
  setSrc: (src: string) => void;
}

export const validationSchema = Yup.object().shape({
  src: Yup.string()
    .min(3, 'Must have at least 3 characters')
    .matches(/^(?!http:\/\/).*/, 'Insecure URLs are not supported')
    .matches(/^(?!https:\/\/).*\..+/, 'Enter a valid URL')
    .required(),
});

export const cleanSrc = (src: string) => {
  return `https://${src.replace(/^https:\/\//, '')}`.trim();
};

export const EmptyState = ({ setSrc }: EmptyStateProps) => {
  const onSubmit = ({ src }: { src: string }) => {
    setSrc(cleanSrc(src));
  };

  return (
    <Container>
      <IFrameIcon size={34} />
      <PlaceholderParagraph size="short" align="center" isTitle noMargin>
        Add IFrame link
      </PlaceholderParagraph>
      <PlaceholderParagraph size="short" align="center">
        Add IFrame link here or on the toolbox.
      </PlaceholderParagraph>
      <Formik initialValues={{ src: '' }} validationSchema={validationSchema} onSubmit={onSubmit}>
        <Form>
          <Inline space="xs">
            <Field name="src">
              {({ field, meta }: FieldAttributes<any>) => (
                <>
                  <ValueBox label="https://">
                    <ValueInput
                      {...field}
                      data-cy="iframe-node-src-input"
                      placeholder="www.example.com"
                      hasError={meta.touched && meta.error}
                      data-craft-ignore-events
                      css={{ width: '200px' }}
                    />
                  </ValueBox>
                  {meta.touched && meta.error && (
                    <InlineMessage>
                      <AlertDangerIcon size="s" color="Danger" />
                      <Feedback ml="xxs" color="danger">
                        {meta.error}
                      </Feedback>
                    </InlineMessage>
                  )}
                </>
              )}
            </Field>
            <Button
              data-cy="iframe-node-add-button"
              variant="secondary"
              type="submit"
              size="small"
              data-craft-ignore-events
            >
              Add
            </Button>
          </Inline>
        </Form>
      </Formik>
    </Container>
  );
};
